import type { JSX } from "react";

import type { Dayjs } from "dayjs";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import {
    ChartTooltipTotal,
    HeadersContainer,
    RowsContainer,
    TotalContainer,
    RowContainer,
    TooltipEntityContainer,
    TooltipRowText,
} from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import type { GraphDataWithZones } from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/types";
import type { IDurationObject } from "app/services/DurationService";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getTooltipHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";

const TooltipWrapperStyled = styled.div<{ width: string }>`
    ${(props) => ({ width: props.width })};
    ${HeadersContainer} > span:not(:first-child) {
        display: block;
        width: 100%;
        text-align: end;
    }
    ${RowsContainer} > div > *:not(:first-child) {
        display: block;
        width: 100%;
        text-align: end;
    }
    ${TotalContainer} ${RowContainer} > span:not(:first-child) {
        text-align: end;
        width: 100%;
    }
    ${RowsContainer} ${TooltipEntityContainer} ${TooltipRowText} {
        width: 180px;
    }
`;

export const graphTooltip = (
    transformedData: GraphDataWithZones,
    selectedGranularity: GranularityNames,
    durationData: IDurationObject<Dayjs>,
    sourceType: string,
) =>
    function (position: number, date: number | string, alertMessage = "", annotationBanner?: JSX.Element) {
        if (transformedData.length === 0) {
            return;
        }

        const totalRow = transformedData.find((legendDataPoint) => legendDataPoint.isTotal === true);
        const rows = transformedData
            .filter((legendDataPoint) => legendDataPoint.visible || legendDataPoint.isTotal)
            .map((legendDataPoint) => {
                const dataIndex = transformedData.findIndex((dataPoint) => dataPoint.name === legendDataPoint.name);

                if (dataIndex > -1) {
                    const rawFirstValue = Math.round(transformedData[dataIndex].data[position].y / 10) * 10;
                    const secondValue = percentageSignFilter()(rawFirstValue / totalRow.data[position].y, 0) as string;

                    return {
                        displayName: legendDataPoint.name,
                        isTotal: legendDataPoint.isTotal,
                        color: legendDataPoint.color,
                        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                        firstValue: abbrNumberFilter()(rawFirstValue, 10),
                        secondValue: <span style={{ color: colorsPalettes.carbon[300] }}>{secondValue}</span>,
                    };
                }
            });

        return (
            <TooltipWrapperStyled width="340px">
                <ChartTooltipTotal
                    title={getTooltipHeader(
                        selectedGranularity,
                        [{ x: date }],
                        durationData.to.valueOf() as unknown as string,
                        false,
                        alertMessage,
                    )}
                    columnsSize="54% 23% 23%"
                    rows={rows.filter((row) => !row.isTotal)}
                    total={rows.find((row) => row.isTotal)}
                    headers={[
                        translate("keywordanalysis.organic2.clicks.over.time.tooltip.domain"),
                        translate(`keywordanalysis.organic2.clicks.over.time.tooltip.${sourceType}.clicks`),
                        translate("keywordanalysis.organic2.clicks.over.time.tooltip.share.of.total"),
                    ]}
                />
                {annotationBanner ? annotationBanner : null}
            </TooltipWrapperStyled>
        );
    };
